import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import {
  listCredentials,
  refreshCredential,
  rotateCredential,
  testCredential,
  type Credential,
  type CredentialHealth,
  type CredentialSink,
  type ExpirySource,
  type RotationResult,
  type SinkState,
} from "@/api/factory";
import { ConfirmModal } from "@/components/ConfirmModal";
import { CopyButton } from "@/components/CopyButton";
import { Field, Input } from "@/components/form";
import { ErrorBox, MutationError } from "@/components/Spinner";
import { RelativeTime } from "@/components/RelativeTime";
import { isProductionPlane } from "@/runtimeConfig";
import { Button, CardSkeleton, LoadingRegion, Notice, Page, PageHeader, StatusPill, UnsavedBadge, type Tone } from "@/components/ui";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { FACTORY } from "@/lib/sections";

// Cinq tons seulement : la page distinguait l'ambre et l'orange, le libellé
// porte désormais la nuance et le ton le niveau d'alerte. « Urgent » (échéance
// imminente) est un danger, pas un avertissement.
const HEALTH_STYLES: Record<CredentialHealth, { tone: Tone; label: string }> = {
  ok: { tone: "success", label: "Valid" },
  warn: { tone: "warning", label: "Needs renewal" },
  critical: { tone: "danger", label: "Urgent" },
  expired: { tone: "danger", label: "Expired" },
  unknown: { tone: "neutral", label: "Unknown expiry" },
};

const SINK_STYLES: Record<SinkState, { tone: Tone; label: string }> = {
  in_sync: { tone: "success", label: "Up to date" },
  stale: { tone: "warning", label: "Behind" },
  drifted: { tone: "warning", label: "Modified outside Factory" },
  // Neutre, et non en alerte : c'est l'état de départ de tout identifiant
  // antérieur à la Fabrique. La première rotation le règle.
  unmanaged: { tone: "neutral", label: "Not yet managed" },
  absent: { tone: "danger", label: "Missing" },
  manual: { tone: "neutral", label: "Manual" },
  unknown: { tone: "neutral", label: "Not observed" },
};

// D'où vient la date affichée. La distinction n'est pas cosmétique : une date
// « déduite » n'est qu'une politique interne, et se fier à elle comme à une
// date publiée par le fournisseur est précisément ce qui rate une expiration.
const SOURCE_LABELS: Record<ExpirySource, string> = {
  discovered: "announced by the provider",
  declared: "entered at rotation",
  policy: "inferred from age",
  unknown: "unknown",
};

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="card">
      <div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div>
      <div className="mt-1 text-3xl font-semibold text-ink">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-ink-muted">{hint}</div>}
    </div>
  );
}

function SinkRow({ sink }: { sink: CredentialSink }) {
  // A state the table does not know yet (the API is ahead of the GUI) shows as
  // itself, neutral: the type only holds at compile time, and reading `.tone` of
  // `undefined` would blank the whole page.
  const style = SINK_STYLES[sink.state] ?? { tone: "neutral" as Tone, label: sink.state };
  return (
    <li className="flex flex-col gap-1 border-t border-line py-2 first:border-t-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="truncate text-sm text-ink">{sink.label}</div>
        {sink.instructions && (
          <div className="mt-1 flex items-start gap-2 text-xs text-ink-muted">
            <span className="flex-1">{sink.instructions}</span>
            <CopyButton value={sink.instructions} label="Copy" />
          </div>
        )}
        {sink.lastError && (
          <div className="mt-1 text-xs text-danger-fg">{sink.lastError}</div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-3 text-xs text-ink-muted">
        {sink.propagatedAt && (
          <span title="Last write by the Factory">
            written <RelativeTime epochMs={sink.propagatedAt} />
          </span>
        )}
        <StatusPill tone={style.tone}>{style.label}</StatusPill>
      </div>
    </li>
  );
}

/**
 * Formulaire de rotation : la valeur ne quitte jamais le composant.
 *
 * What is pasted here is a SECRET, and the form holds it only in its own state (a password field): not in a
 * storage, a log, a `title`, a key, an attribute, nor in the text of the dialog that asks about it. It is
 * still an input worth protecting — a token created at the provider a minute ago, pasted from a password
 * manager, that the operator would have to go and fetch again — so the way out of the page is held while
 * the field has something in it, and Cancel asks before it throws it away, like the pool editor does.
 * The dirty flag is computed from the two fields by looking at them, NOT through `useDirtyValues`, which
 * keeps a snapshot of the values it compares: a snapshot of a secret is a copy of it. An empty field, or
 * one that only holds spaces (the form ignores it too), is not a change.
 */
function RotateForm({
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  pending: boolean;
  /** Where the failure of the rotation is said: in the form that asked for it. */
  error?: ReactNode;
  onSubmit: (value: string, declaredExpiresAt: number | null) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState("");
  const [expiry, setExpiry] = useState("");
  const [discarding, setDiscarding] = useState(false);
  const dirty = value.trim() !== "" || expiry !== "";
  useUnsavedChanges(dirty);

  return (
    <>
      <form
        className="mt-3 space-y-3 rounded-md border border-line bg-sunken p-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!value.trim()) return;
          const parsed = expiry ? Date.parse(`${expiry}T12:00:00Z`) : NaN;
          onSubmit(value.trim(), Number.isNaN(parsed) ? null : parsed);
        }}
      >
        <Field
          label="New value"
          help="The value is verified with the provider before being written anywhere. If it's rejected, nothing is changed."
        >
          <Input
            mono
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Paste the freshly created token"
          />
        </Field>
        <Field
          label="Expiry date (optional)"
          help="Fill this in when the provider doesn't publish an expiry date — without it, the alert relies on age alone."
        >
          <Input
            type="date"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
          />
        </Field>
        {error}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" type="submit" loading={pending} disabled={!value.trim()}>
            {pending ? "Propagating…" : "Propagate everywhere"}
          </Button>
          <Button variant="secondary" disabled={pending} onClick={() => (dirty ? setDiscarding(true) : onCancel())}>
            Cancel
          </Button>
          <UnsavedBadge dirty={dirty} />
        </div>
      </form>
      {discarding && (
        <ConfirmModal
          title="Discard what you typed?"
          confirmLabel="Discard"
          cancelLabel="Keep editing"
          danger
          onCancel={() => setDiscarding(false)}
          onConfirm={() => {
            setDiscarding(false);
            onCancel();
          }}
        >
          <p>The new value has not been sent anywhere. Cancelling drops what you pasted, and you will have to paste it again.</p>
        </ConfirmModal>
      )}
    </>
  );
}

function CredentialCard({
  credential,
  writable,
  onResult,
}: {
  credential: Credential;
  writable: boolean;
  onResult: (result: RotationResult | string) => void;
}) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const qc = useQueryClient();
  const [rotating, setRotating] = useState(false);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["factory", "credentials"] });

  const rotate = useMutation({
    mutationFn: (input: { value?: string; declaredExpiresAt?: number | null }) =>
      rotateCredential(token, credential.id, input),
    onSuccess: (result) => {
      setRotating(false);
      onResult(result);
      invalidate();
    },
  });

  const probe = useMutation({
    mutationFn: () => testCredential(token, credential.id),
    onSuccess: (result) => {
      onResult(result.message);
      invalidate();
    },
  });

  const refresh = useMutation({
    mutationFn: () => refreshCredential(token, credential.id),
    onSuccess: () => {
      onResult("Sinks re-observed.");
      invalidate();
    },
  });

  // Same fallback as the sinks: an unknown health shows as itself, neutral.
  const health = HEALTH_STYLES[credential.health] ?? {
    tone: "neutral" as Tone,
    label: credential.health,
  };
  const busy = rotate.isPending || probe.isPending || refresh.isPending;
  // Each action has ITS failure, said by its title, beside its own buttons — never one shared box
  // (``rotate.error ?? probe.error ?? …`` showed the first that was set, so a stale failure of one
  // action hid the fresh failure of another). Running any action clears the failures of the others,
  // so that at most one is on screen, and it is the last.
  const run = (start: () => void) => {
    rotate.reset();
    probe.reset();
    refresh.reset();
    start();
  };

  return (
    <article className="card">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-base font-semibold text-ink">
            {credential.label}
            {credential.bootstrap && (
              <StatusPill tone="neutral" dot={false}>
                Bootstrap
              </StatusPill>
            )}
          </h2>
          <p className="mt-1 max-w-prose text-sm text-ink-muted">
            {credential.description}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <StatusPill tone={health.tone}>{health.label}</StatusPill>
          <span className="text-xs text-ink-muted">
            {credential.expiresAt
              ? <>expires <RelativeTime epochMs={credential.expiresAt} deadline /></>
              : "no known expiry"}
          </span>
          {/* Where the date comes from, under the date: italic, not a lighter grey (the old one
              was 2.6:1) — it is a note on the line above, and it must stay legible. */}
          <span className="text-xs italic text-ink-muted">
            {SOURCE_LABELS[credential.expirySource]}
          </span>
        </div>
      </header>

      {credential.bootstrap && credential.health !== "ok" && (
        <Notice tone="warning" className="mt-3">
          Renew this one first: without it, the Factory can't write anything
          on the GitHub side anymore — not even its own value.
        </Notice>
      )}

      <ul className="mt-3">
        {credential.sinks.map((sink) => (
          <SinkRow key={sink.id} sink={sink} />
        ))}
      </ul>

      {/* While the renewal form is open it says its own failure. */}
      {!rotating && <MutationError mutation={rotate} title="Could not propagate the credential" className="mt-3" />}
      <MutationError mutation={probe} title="Could not test the credential" className="mt-3" />
      <MutationError mutation={refresh} title="Could not re-observe the sinks" className="mt-3" />

      {writable && (
        <div className="mt-3 flex flex-wrap gap-2">
          {!rotating && (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => setRotating(true)}
            >
              Renew
            </Button>
          )}
          {credential.testable && (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => run(() => probe.mutate())}
              title="Tests the stored value, not the one in the form"
            >
              {probe.isPending ? "Testing…" : "Test"}
            </Button>
          )}
          {credential.repropagatable && (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => run(() => rotate.mutate({}))}
              title="Rewrites the existing value to every sink"
            >
              {rotate.isPending && !rotating ? "Propagating…" : "Re-propagate"}
            </Button>
          )}
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => run(() => refresh.mutate())}
          >
            {refresh.isPending ? "Observing…" : "Re-observe"}
          </Button>
        </div>
      )}

      {rotating && (
        <RotateForm
          pending={rotate.isPending}
          error={<MutationError mutation={rotate} title="Could not propagate the credential" />}
          onCancel={() => {
            rotate.reset();
            setRotating(false);
          }}
          onSubmit={(value, declaredExpiresAt) =>
            run(() => rotate.mutate({ value, declaredExpiresAt }))
          }
        />
      )}

      {credential.lastProbe?.at && (
        <p className="mt-3 text-xs text-ink-muted">
          Last test <RelativeTime epochMs={credential.lastProbe.at} /> —{" "}
          {credential.lastProbe.ok ? "accepted" : "rejected"}:{" "}
          {credential.lastProbe.message}
        </p>
      )}
    </article>
  );
}

export function FactoryCredentialsPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const writable = isProductionPlane();
  const [notice, setNotice] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["factory", "credentials"],
    queryFn: () => listCredentials(token),
    enabled: !!token,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const summary = query.data?.summary;

  function handleResult(result: RotationResult | string) {
    if (typeof result === "string") {
      setNotice(result);
      return;
    }
    const failed = result.results.filter((r) => !r.ok && !r.skipped);
    setNotice(
      failed.length === 0
        ? `${result.written} sink(s) written.`
        : `${result.written}/${result.total} written. Remaining: ${failed
            .map((r) => r.label)
            .join(", ")}.`,
    );
  }

  return (
    <Page>
      <PageHeader
        section={FACTORY}
        title="Credentials"
        subtitle="The long-lived tokens that deployments and CI depend on: what's approaching its expiry, and whether the last rotation actually reached every sink. No value is readable from this page — only dates."
      />

      <div className="space-y-6">
        {!writable && (
          <p className="rounded-md border border-line bg-surface p-3 text-sm text-ink-muted">
            Development environment: read-only. Renewal is done from the
            production control plane.
          </p>
        )}

        {notice && (
          <div className="flex items-start justify-between gap-3 rounded-md border border-line bg-surface p-3 text-sm text-ink">
            <span>{notice}</span>
            <Button variant="ghost" onClick={() => setNotice(null)}>
              Close
            </Button>
          </div>
        )}

        {query.isLoading && (
          <LoadingRegion className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </LoadingRegion>
        )}
        {query.isError && <ErrorBox error={query.error} />}

        {summary && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard label="Credentials tracked" value={summary.total} />
            <StatCard
              label="Needs renewal"
              value={summary.needsAttention}
              hint="expired, urgent, or nearing expiry"
            />
            <StatCard
              label="Sinks needing attention"
              value={summary.unhealthySinks}
              hint="missing, behind, or modified outside the Factory"
            />
          </div>
        )}

        <div className="space-y-4">
          {items.map((credential) => (
            <CredentialCard
              key={credential.id}
              credential={credential}
              writable={writable}
              onResult={handleResult}
            />
          ))}
        </div>

        {query.data && query.data.githubScope.length > 0 && (
          <div className="card">
            <h2 className="text-sm font-semibold text-ink">
              Expected scope of the bootstrap token
            </h2>
            <p className="mt-1 text-xs text-ink-muted">
              This token must cover these repos, and no others. A repo outside
              its scope responds 404, not 403: a "repository not found" error
              almost always points to this setting, not a missing repo.
            </p>
            <ul className="mt-2 space-y-1 text-xs text-ink">
              {query.data.githubScope.map((entry) => (
                <li key={entry.repo} className="flex flex-wrap items-center gap-2">
                  <span className="font-mono">{entry.repo}</span>
                  {entry.reasons.map((reason) => (
                    <StatusPill
                      key={reason}
                      tone={reason === "secrets" ? "neutral" : "info"}
                      dot={false}
                    >
                      {reason === "secrets"
                        ? "Secrets: read/write"
                        : "Administration: read/write"}
                    </StatusPill>
                  ))}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-ink-muted">
              The permissions differ: "Secrets" is for writing an Actions
              secret, "Administration" for reading and removing a runner. A
              repo that consumes the fleet without holding a secret only
              needs the latter.
            </p>
          </div>
        )}
      </div>
    </Page>
  );
}
